/**
 * @fileoverview PageError component for displaying error messages with optional refresh button
 * @module PageError
 * @requires prop-types
 * @requires @smarterservices/smarter-ui
 * @requires ../SinglePane
 */

import PropTypes from 'prop-types';
import {
  Flex,
  View,
  Text,
  IconWarningLine,
  Button
} from '@smarterservices/smarter-ui';
import SinglePane from '../SinglePane';
import StepInstructions from '../Common/StepInstructions';

/**
 * A component for displaying error messages with an optional refresh button
 *
 * @function PageError
 * @param {Object} props - The component props
 * @param {string} props.errorText - The error message to display
 * @param {boolean} [props.showRefreshButton=true] - Whether to show the refresh button
 * @param {string} [props.refreshButtonText='Refresh'] - Text to display on the refresh button
 * @returns {React.ReactElement} The rendered component
 */
const PageError = ({
  errorText, 
  errorTextReplacements = {},
  showRefreshButton = true, 
  refreshButtonText = 'Refresh', 
  refreshIntroText = 'Click refresh to try again. If this problem persists please reach out to support.',
  maxWidth = '400px',
  errorCode}) => {
  /**
   * Renders the error message with optional refresh button
   */
  return (
    <SinglePane>
      <Flex as="div" direction="column" alignItems="center" justifyContent="center" height="100%" width="100%">
        <View
          as="div"
          margin="auto"
          textAlign="center"
          maxWidth={maxWidth}
        >
          <View
            as="div"
            className="page-error-content"
            margin="auto"
            textAlign="center"
            borderRadius="large"
            padding="large"
            borderWidth="small"
            borderColor="primary"
            background="primary"
            maxWidth={maxWidth}
          >
            <View as="div" margin="0 0 medium 0" textAlign="center">
              <IconWarningLine color="warning" size="large" />
            </View>
            <Text size="medium">
              <StepInstructions
                title="Page Error Information"
                instructions={errorText}
                replacements={errorTextReplacements}
              />
            </Text>
            {showRefreshButton && (
              <View as="div" margin="medium 0 0 0" textAlign="center">
                <Text as="p" size="small">
                  {refreshIntroText}
                </Text>
                <Button
                  size="small"
                  color="primary"
                  margin="small 0 0 0"
                  onClick={() => window.location.reload()}
                >
                  {refreshButtonText}
                </Button>
              </View>
            )}
          </View>
          {errorCode && (
            <View as="div" margin="x-small 0 0 0" textAlign="center">
              <Text as="p"
                    size="x-small"
                    color="secondary"
                    themeOverride={{
                      fontSizeXSmall: '9px'
                    }}>
                reference code: {errorCode}
              </Text>
            </View>
          )}
        </View>
      </Flex>
    </SinglePane>
  );
};

PageError.propTypes = {
  /** The error message to display, can be a string or array of objects */
  errorText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.object)
  ]).isRequired,
  /** Whether to show the refresh button */
  showRefreshButton: PropTypes.bool,
  /** Text to display on the refresh button */
  refreshButtonText: PropTypes.string,
  /** Error code to display */
  errorCode: PropTypes.string,
  /** Maximum width of the error message */
  maxWidth: PropTypes.string
};

PageError.defaultProps = {
  showRefreshButton: true,
  refreshButtonText: 'Refresh',
  errorCode: null,
  maxWidth: '400px'
};

export default PageError;